// src/components/pages/EventManager/organisms/ChallengeList.js

import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { db } from "../../../../firebase";
import { collection, doc, setDoc, deleteDoc, getDocs } from "firebase/firestore";
import { fetchHomePageChallenges, fetchSubmissions } from "../../../../services/challengeService";
import CustomButton from "../atoms/CustomButton";

const ChallengeList = ({ theme, event }) => {
  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);

  const toggleSelect = (id) => {
    setSelectedSubmissions((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    if (submissions.every((s) => selectedSubmissions.includes(s.id))) {
      setSelectedSubmissions([]);
    } else {
      setSelectedSubmissions(submissions.map((s) => s.id));
    }
  };

  const handleSave = async () => {
    try {
      const contentCol = collection(db, "events", event.id, "content");
      const current = await getDocs(contentCol);

      // Current docs for this challenge
      const currentForChallenge = current.docs.filter((d) => d.data().challengeId === selectedChallenge.id);
      const currentIds = new Set(currentForChallenge.map((d) => d.id));

      // Add/update selected
      await Promise.all(
        submissions
          .filter((s) => selectedSubmissions.includes(s.id))
          .map(async (s) => {
            await setDoc(doc(contentCol, s.id), { ...s }, { merge: true });
          })
      );

      // Remove deselected that belong to this challenge only (unlinking)
      await Promise.all(
        [...currentIds]
          .filter((id) => !selectedSubmissions.includes(id))
          .map(async (id) => {
            await deleteDoc(doc(contentCol, id));
          })
      );

      handleClose();
    } catch (err) {
      console.error("Error updating content subcollection:", err);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const all = await fetchHomePageChallenges();
        const withAtLeastOne = await Promise.all(
          all.map(async (ch) => {
            const { submissions } = await fetchSubmissions(ch.id, { limitNumber: 1 });
            return submissions && submissions.length > 0 ? ch : null;
          })
        );
        setChallenges(withAtLeastOne.filter(Boolean));
      } catch (err) {
        console.error("Error fetching challenges or submissions:", err);
      }
    };
    load();
  }, []);

  const handleSelectChallenge = async (challenge) => {
    setSelectedChallenge(challenge);
    setOpen(true);
    try {
      const { submissions } = await fetchSubmissions(challenge.id);
      setSubmissions(submissions);

      const contentCol = collection(db, "events", event.id, "content");
      const current = await getDocs(contentCol);
      const savedIds = new Set(current.docs.map((d) => d.id));
      setSelectedSubmissions(submissions.map((s) => s.id).filter((id) => savedIds.has(id)));
    } catch (err) {
      console.error("Error fetching submissions or content:", err);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedChallenge(null);
    setSubmissions([]);
    setSelectedSubmissions([]);
  };

  return (
    <>
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" sx={{ fontFamily: "basic-sans", fontWeight: 700, color: "white" }}>
          Select Event Content by Challenge
        </Typography>
      </Box>

      <Grid container spacing={4} sx={{ mt: 1 }}>
        {challenges.map((ch) => (
          <Grid item xs={12} sm={6} md={3} key={ch.id} sx={{ display: "flex" }}>
            <Card
              sx={{
                backgroundColor: theme.colors.mediumPurple,
                color: "white",
                fontFamily: "basic-sans",
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: "20px",
              }}
            >
              {ch.hero && <CardMedia component="img" height="140" image={ch.hero} alt={ch.title} />}
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontFamily: "basic-sans" }}>
                  {ch.title}
                </Typography>
              </CardContent>
              <CardActions>
                <CustomButton
                  theme={theme}
                  onClick={() => handleSelectChallenge(ch)}
                  sx={{
                    borderRadius: "50px",
                    backgroundColor: theme.colors.darkBlue,
                    "&:hover": { backgroundColor: theme.colors.amber },
                  }}
                >
                  Select
                </CustomButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Submissions Modal (styled like the others) */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: theme.colors.mediumPurple,
            color: "white",
            fontFamily: "basic-sans",
            border: `1px solid ${theme.colors.palePurple}`,
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "basic-sans",
            color: theme.colors.amber,
            backgroundColor: theme.colors.headerPurple,
            fontWeight: 700,
            borderBottom: `1px solid ${theme.colors.palePurple}`,
            pr: 6,
          }}
        >
          Submissions for: {selectedChallenge?.title}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.colors.amber,
              "&:hover": { color: theme.colors.green },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: theme.colors.mediumPurple, borderColor: theme.colors.palePurple }}>
          {submissions.length === 0 ? (
            <Typography sx={{ fontFamily: "basic-sans" }}>No submissions found.</Typography>
          ) : (
            <Grid container spacing={2}>
              {submissions.map((s) => {
                const thumb = s.submissionSaleId
                  ? `https://swayer.blob.core.windows.net/${s.submissionSaleId}-pub/_thumb_large.jpg`
                  : null;
                const selected = selectedSubmissions.includes(s.id);
                return (
                  <Grid item key={s.id} xs={12} sm={6} md={4} sx={{ display: "flex" }}>
                    <Card
                      onClick={() => toggleSelect(s.id)}
                      sx={{
                        fontFamily: "basic-sans",
                        backgroundColor: theme.colors.mediumPurple,
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "20px",
                        border: selected ? `2px solid ${theme.colors.green}` : `2px solid ${theme.colors.darkBlue}`,
                        cursor: "pointer",
                      }}
                    >
                      {thumb && (
                        <CardMedia
                          component="div"
                          sx={{
                            paddingTop: "56.25%",
                            backgroundImage: `url(${thumb})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                          alt={s.title || "Submission Thumbnail"}
                        />
                      )}
                      <CardContent>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "basic-sans", color: "white", fontWeight: 700 }}
                        >
                          {s.title || s.id}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>

        <DialogActions
          sx={{ backgroundColor: theme.colors.mediumPurple, borderTop: `1px solid ${theme.colors.palePurple}` }}
        >
          <Button
            onClick={handleSelectAll}
            sx={{ color: theme.colors.amber, "&:hover": { color: theme.colors.green, background: "transparent" } }}
          >
            {submissions.length > 0 && submissions.every((s) => selectedSubmissions.includes(s.id))
              ? "Clear Selection"
              : "Select All"}
          </Button>

          <Button
            onClick={handleSave}
            disabled={selectedSubmissions.length === 0}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "5px",
              backgroundColor:
                selectedSubmissions.length === 0 ? `${theme.colors.palePurple} !important` : theme.colors.green,
            }}
          >
            Save
          </Button>

          <Button
            onClick={handleClose}
            sx={{ color: theme.colors.amber, "&:hover": { color: theme.colors.green, background: "transparent" } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChallengeList;
